/**
 * Whether one person matches, on the fields a separation is actually reasoned about.
 *
 * CASE-INSENSITIVE ON EVERY TEXT FIELD, because the caller is repeating a value it read in a summary
 * or heard in a sentence, and "finance" losing to "Finance" is a failure nobody can see. Exact match
 * on the categorical fields and `includes` on the job title — a title is prose and a level value is
 * a label, so they are matched the way each is written.
 */
export function matchesPeopleFilter(person, filter, bucketOf = () => 'Undecided') {
    const same = (a, b) => b === undefined || (a ?? '').toLowerCase() === b.toLowerCase();
    if (!filter.includeInactive && (person.inactive || person.isLeaver))
        return false;
    if (!filter.includeOpenPositions && person.openPosition)
        return false;
    if (filter.level !== undefined || filter.levelValue !== undefined) {
        /*
          BOTH HALVES OR NEITHER IS NOT ENFORCED, and that is deliberate. A value on its own — "Finance",
          no level — is a request nobody should have to spell twice, and the hierarchy is per project so
          the caller is often guessing at the key. A value with no level therefore matches that value in
          ANY level, which is what a person means when they say it.
        */
        const entries = Object.entries(person.levels ?? {});
        const found = entries.some(([key, value]) => (filter.level === undefined || key.toLowerCase() === filter.level.toLowerCase()) &&
            (filter.levelValue === undefined ||
                (value ?? '').toLowerCase() === filter.levelValue.toLowerCase()));
        if (!found)
            return false;
    }
    if (filter.jobTitleContains !== undefined &&
        !(person.jobTitle ?? '').toLowerCase().includes(filter.jobTitleContains.toLowerCase())) {
        return false;
    }
    if (!same(person.location, filter.location))
        return false;
    if (!same(person.country, filter.country))
        return false;
    if (!same(person.seniority, filter.seniority))
        return false;
    if (!same(person.businessUnit, filter.businessUnit))
        return false;
    if (!same(person.legalEntity, filter.legalEntity))
        return false;
    /*
      A PERSON WITH NO DEDICATION IS NOT A PERSON WITH ZERO. `null` means unknown — the file did not
      say — and treating it as 0 would put every unmeasured person into "nobody serves the carve-out",
      which is the reading `domain.ts` warns about for the shared marker and is wrong the same way. So a
      dedication bound excludes them rather than answering for them.
    */
    if (filter.dedicationAtLeast !== undefined) {
        if (person.dedication == null || person.dedication < filter.dedicationAtLeast)
            return false;
    }
    if (filter.dedicationAtMost !== undefined) {
        if (person.dedication == null || person.dedication > filter.dedicationAtMost)
            return false;
    }
    if (filter.bucket !== undefined && bucketOf(person.id) !== filter.bucket)
        return false;
    return true;
}
/**
 * Whether a person is inside a member's access. `null` scope is the whole organisation.
 *
 * THE TWO `true`s ARE THE POINT OF THIS FUNCTION, and without them the scope would silently drop
 * every leaver and inactive row from a scoped member's roster. `peopleFilterSchema` DEFAULTS
 * `includeInactive` to `false`, because a filter answering a question about a population should
 * answer the same question the panes on screen answer. A scope object an owner built has no such
 * key, so passing it to the raw predicate would take the falsy default — and those people are IN the
 * roster and OUT of every aggregate, so removing them from the payload would change the denominator
 * of every count that member sees. It would look like a scope decision and be a schema default.
 *
 * `bucket` IS ABSENT FOR A HARDER REASON. It is per-scenario decision state, so a permission keyed on
 * it would MOVE when somebody made a decision — a person visible in Scenario A and not in Scenario B,
 * changing under you mid-session, and re-deciding somebody's access as a side effect of designing.
 * A permission has to be a function of the roster only.
 */
export function inScope(person, scope) {
    if (scope === null)
        return true;
    return matchesPeopleFilter(person, {
        ...scope,
        includeInactive: true,
        includeOpenPositions: true,
    });
}
export const PERSON_SALARY_POLICY = {
    id: 'keep',
    name: 'keep',
    supervisorId: 'keep',
    jobTitle: 'keep',
    location: 'keep',
    country: 'keep',
    // A code, not an amount: revealing EUR beside a withheld salary reveals nothing about magnitude.
    currency: 'keep',
    // A scheme label, same reasoning — the amounts pensions imply live with the actuaries, not here.
    pensionScheme: 'keep',
    seniority: 'keep',
    businessUnit: 'keep',
    legalEntity: 'keep',
    /** The whole point of the axis. */
    salary: 'withheld',
    fte: 'keep',
    dedication: 'keep',
    sharedMarker: 'keep',
    openPosition: 'keep',
    inactive: 'keep',
    isLeaver: 'keep',
    isBuildUp: 'keep',
    levels: 'keep',
    /** The source row, which on a real export carries compensation under the client's own column name. */
    raw: 'withheld',
};
const _everySalaryFieldIsDecided = true;
void _everySalaryFieldIsDecided;
/** The fields withheld, derived from the table so the two cannot disagree. */
export const WITHHELD_PERSON_FIELDS = Object.entries(PERSON_SALARY_POLICY)
    .filter(([, policy]) => policy === 'withheld')
    .map(([field]) => field);
/**
 * One person with compensation removed.
 *
 * `salary: null` rather than the key being absent, because `Person.salary` is `number | null` and
 * null already means "the file did not say" everywhere in the engine. The product ALREADY HAS a
 * no-salary mode for exactly that case — `diagnostics.ts` reports it, `analytics.ts` computes
 * `anySalary`, and `LevelPane` branches on it — so a withheld member lands on a path that is shipped
 * and tested rather than a new one. What is NOT reused is the sentence: a file with no salary column
 * is the client's doing and this is not, so the reader is told which.
 */
export function withholdSalary(person) {
    return { ...person, salary: null, raw: null };
}
export const DESIGN_SCOPE_POLICY = {
    buckets: 'per-employee',
    bucketMeta: 'per-employee',
    strandedPlan: 'per-employee',
    /** perspective → groupNodeId → employeeId. The person is the value, and may be outside the scope. */
    heads: 'per-employee-value',
    /**
     * `empOverrides` is employeeId → field → value, and one of those fields is `salary`. A member who
     * may not see compensation must not receive it through the per-scenario overlay either — which is
     * the same class of miss as `raw`: the number arrives under a different key in a structure nothing
     * on screen labels as compensation.
     */
    scenarioOverrides: 'salary-bearing',
    /** A planned hire is a `Person`: it has a salary, and it names the manager it reports to. */
    buildUps: 'salary-bearing',
    perimeterThreshold: 'project-wide',
    tsas: 'project-wide',
};
const _everyDesignFieldIsDecided = true;
void _everyDesignFieldIsDecided;
/** Keep only the entries whose key is a person still in the payload. */
function keepKeyed(map, visible) {
    const out = Object.create(null);
    for (const [id, value] of Object.entries(map))
        if (visible.has(id))
            out[id] = value;
    return out;
}
/**
 * Narrow a whole state to what one member may see.
 *
 * RETURNS THE SAME OBJECT BY REFERENCE when the member is unrestricted, which is every membership
 * until an owner grants otherwise. That identity is not an optimisation: `tests/parity` compares
 * 603,176 values through this payload, and a state that is not rebuilt cannot have changed shape.
 *
 * PURE, AND IN CORE rather than beside the route, so the browser can apply the same narrowing to a
 * state it already holds and the two cannot disagree about what a scope means.
 */
export function narrowState(state, access) {
    const scoped = access.scope !== null;
    if (!scoped && !access.salaryHidden)
        return state;
    const employees = scoped ? state.employees.filter((p) => inScope(p, access.scope)) : state.employees;
    const visible = new Set(employees.map((p) => p.id));
    const design = state.design;
    /*
      The visible people whose manager is not — computed here because this is the only place that knows
      both sets. See `StateScopeNotice.withheldSupervisorOf` for why it carries the SUBORDINATE's id.
    */
    const withheldSupervisorOf = scoped
        ? employees.filter((p) => p.supervisorId !== null && !visible.has(p.supervisorId)).map((p) => p.id)
        : [];
    const next = {
        ...state,
        scope: {
            filter: access.scope ?? null,
            salaryWithheld: access.salaryHidden,
            withheldSupervisorOf,
        },
        employees: access.salaryHidden ? employees.map(withholdSalary) : employees,
        /*
          `rows` is the untouched source spreadsheet and carries every column the client exported,
          compensation included. `loadSeparationState` leaves it empty deliberately, so this is belt and
          braces — and it is the kind of belt that matters, because the day something starts populating
          it, this is the line that stops it being a hole.
        */
        rows: access.salaryHidden ? [] : state.rows,
        design: narrowDesign(design, visible, access),
        targetOverrides: narrowOverrides(state.targetOverrides, visible, access),
    };
    return next;
}
/**
 * One DESIGN through the same narrowing `narrowState` applies to the design it holds.
 *
 * Exported because a design is not always inside a state: `/scenario-compare` and the deck's
 * compare slide price every SIBLING design over one narrowed roster, and a sibling passed to the
 * engines raw would put the build-ups a scope withholds back into a scoped member's totals —
 * numbers every pane on their screen computes without. Same visible set, same rule, one function,
 * so the row a scoped member reads here is the row their own `/design` would price.
 */
export function narrowDesign(design, visible, access) {
    const scoped = access.scope !== null;
    if (!scoped && !access.salaryHidden)
        return design;
    const buildUps = narrowBuildUps(design.buildUps, visible, access);
    /*
      A KEPT HIRE KEEPS ITS DECISION, and this line is a bug fix rather than a nicety.
  
      `visible` is built from `state.employees` alone — it is the roster the scope admits — while a
      build-up's bucket is a decision row keyed by the BUILD-UP's own id, exactly like an employee's.
      So `keepKeyed(design.buckets, visible)` used to delete the bucket of every planned hire,
      including the ones `narrowBuildUps` had just decided this member may see. `currentBucket` then
      defaulted each of them to Remain, and a scoped member's carve-out hires silently left the
      carve-out: out of their Day-1 and Day-2 bridge, out of the carve-side hiring cost, into
      RemainCo's. Nothing said so, because a default is not an error.
  
      The keyed sets are therefore the roster PLUS the hires that survived, and only those — a hire
      dropped by the scope takes its decision with it, which is the rule this function exists for.
      `strandedPlan` is keyed by employees only (`strandedEligible` refuses a build-up), so widening
      the set cannot admit anything there; it is passed the same set so there is one rule and not two.
    */
    const keyed = scoped ? new Set([...visible, ...buildUps.map((b) => String(b.id))]) : visible;
    return {
        ...design,
        buckets: scoped ? keepKeyed(design.buckets, keyed) : design.buckets,
        bucketMeta: scoped ? keepKeyed(design.bucketMeta, keyed) : design.bucketMeta,
        strandedPlan: scoped ? keepKeyed(design.strandedPlan, keyed) : design.strandedPlan,
        // Heads name PEOPLE, so the roster's own set: a hire is not a head designation's subject.
        heads: scoped ? narrowHeads(design.heads, visible) : design.heads,
        scenarioOverrides: narrowOverrides(design.scenarioOverrides, visible, access),
        buildUps,
    };
}
/**
 * A head whose person is outside the scope becomes an explicit null, not a dropped key.
 *
 * ROW PRESENCE IS THE DISTINCTION THE HEADS MODEL TURNS ON — `loadScenarioDesign` says so — because
 * "no head has been chosen" and "explicitly nobody" are different states. Dropping the key would
 * report the first, so a scoped reader would see a group that looks undecided and might decide it,
 * overwriting a designation somebody made. Null says what is true: there is a head, and it is not
 * yours to see.
 */
function narrowHeads(heads, visible) {
    const out = Object.create(null);
    for (const [perspective, byGroup] of Object.entries(heads)) {
        const kept = Object.create(null);
        for (const [groupId, employeeId] of Object.entries(byGroup)) {
            kept[groupId] = employeeId !== null && !visible.has(employeeId) ? null : employeeId;
        }
        out[perspective] = kept;
    }
    return out;
}
function narrowOverrides(all, visible, access) {
    if (access.scope === null && !access.salaryHidden)
        return all;
    const out = Object.create(null);
    for (const [key, overrides] of Object.entries(all)) {
        const emp = Object.create(null);
        for (const [id, fields] of Object.entries(overrides.empOverrides)) {
            if (access.scope !== null && !visible.has(id))
                continue;
            if (!access.salaryHidden) {
                emp[id] = fields;
                continue;
            }
            // The salary override, which is compensation arriving under a per-scenario key.
            const { salary: _dropped, ...rest } = fields;
            emp[id] = rest;
        }
        out[key] = { ...overrides, empOverrides: emp };
    }
    return out;
}
/**
 * A planned hire is narrowed by BOTH axes, and by its supervisor rather than by itself.
 *
 * A hire has no place in the organisation except the manager it reports to, so "is this inside my
 * slice" is a question about that manager. One with no supervisor at all is kept: it is a position
 * at the top of a design, and hiding it from a scoped reader would remove a cost from their totals
 * with nothing to attribute the difference to.
 */
function narrowBuildUps(buildUps, visible, access) {
    const kept = access.scope === null
        ? buildUps
        : buildUps.filter((b) => b.supervisorId === null || visible.has(b.supervisorId));
    return access.salaryHidden ? kept.map((b) => ({ ...b, salary: null, raw: null })) : kept;
}
/**
 * The metrics a surface may offer, given what this payload withheld.
 *
 * `cost` IS DROPPED RATHER THAN DISABLED when compensation was withheld, because
 * `metricFn('cost')` is `Number(p.salary) || 0` — so a member with salary withheld who pressed it
 * would get a chart of zeroes with a real axis and a real caption. That is not a blank; it is a
 * confident wrong answer, and the same argument `TAB_NEEDS_SALARY` makes for the three money panes.
 *
 * IN CORE SO THE RULE IS ONE RULE. Four panes draw this control, and four copies of
 * `keys.filter(k => k !== 'cost')` is four places for the next one to be forgotten.
 */
export function offeredMetrics(state, keys) {
    if (state.scope?.salaryWithheld !== true)
        return keys;
    return keys.filter((k) => k !== 'cost');
}
/**
 * A scope as a sentence somebody can read — "Finance · Germany".
 *
 * IN CORE SO THE WORDING IS ONE WORDING. The banner, the export stamp and the members dialog all name
 * the same scope, and three hand-written joins would drift the first time a field is added.
 *
 * IT NAMES THE FILTER AND NEVER A DENOMINATOR, which is the decision this function exists to make
 * unmissable. "412 of 5,180" would put the size of the hidden population in the label meant to
 * protect it — recoverable by subtraction from any total beside it — so the label conveys
 * PARTIALITY without conveying SCALE. Null when only salary was withheld, which is a different
 * sentence in a different place.
 */
const SCOPE_FIELD_LABELS = {
    levelValue: '',
    level: '',
    jobTitleContains: 'title contains',
    location: 'location',
    country: 'country',
    seniority: 'grade',
    businessUnit: 'business unit',
    legalEntity: 'legal entity',
    dedicationAtLeast: 'dedication at least',
    dedicationAtMost: 'dedication at most',
};
export function describeScope(filter) {
    if (filter === null)
        return null;
    const parts = [];
    for (const [key, value] of Object.entries(filter)) {
        if (value === undefined || value === null || value === '')
            continue;
        // The level key is a coordinate, not a value: "lvl2" says nothing to a reader, and the value
        // beside it says everything. So `levelValue` is shown bare and `level` is not shown at all.
        if (key === 'level')
            continue;
        const label = SCOPE_FIELD_LABELS[key];
        parts.push(label === undefined || label === '' ? String(value) : `${label} ${String(value)}`);
    }
    return parts.length > 0 ? parts.join(' · ') : null;
}
//# sourceMappingURL=scope.js.map